"use client";

import { useEffect } from "react";
import styles from "./reveal.module.css";

const MARGIN = "0px 0px -10% 0px";
const SELECTOR = `.${styles.reveal}`;

const revealablesIn = (node: Node) =>
  node instanceof Element
    ? [
        ...(node.matches(SELECTOR) ? [node] : []),
        ...node.querySelectorAll(SELECTOR),
      ]
    : [];

const Reveal = () => {
  useEffect(() => {
    const show = (node: Element) => node.classList.add(styles.in!);
    const observer =
      "IntersectionObserver" in window
        ? new IntersectionObserver(
            (entries, self) => {
              for (const entry of entries) {
                if (!entry.isIntersecting) {
                  continue;
                }

                show(entry.target);
                self.unobserve(entry.target);
              }
            },
            { rootMargin: MARGIN },
          )
        : null;
    const watch = (node: Element) =>
      observer ? observer.observe(node) : show(node);

    document.querySelectorAll(SELECTOR).forEach(watch);

    // Sections inside a Suspense boundary stream in after this effect has run.
    const arrivals = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => revealablesIn(node).forEach(watch));
      }
    });

    arrivals.observe(document.body, { childList: true, subtree: true });

    return () => {
      arrivals.disconnect();
      observer?.disconnect();
    };
  }, []);

  return null;
};

export default Reveal;
